import { fetchWinBackOffersAnalytics } from 'features/reports/winBackOffersReportSlice'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useCurrentCountry } from 'hooks/miscHooks'
import { useEffect } from 'react'

const MetricCard = ({ label, value }: { label: string; value: string | number }) => (
    <div className="flex min-w-[150px] flex-1 flex-col gap-1 rounded-lg border border-grey-200 bg-white p-4">
        <span className="text-2xl font-bold text-grey-900">{value}</span>
        <span className="text-md text-grey-700">{label}</span>
    </div>
)

interface BreakdownRow {
    label: string
    shown: number
    accepted: number
    acceptance_rate: number
}

const BreakdownCard = ({
    title,
    firstColLabel,
    rows,
}: {
    title: string
    firstColLabel: string
    rows: BreakdownRow[]
}) => (
    <div className="min-w-[320px] flex-1 rounded-lg border border-grey-200 bg-white p-4">
        <h4 className="mb-3 text-md font-bold text-grey-900">{title}</h4>
        <table className="w-full text-md">
            <thead>
                <tr className="text-left text-grey-700">
                    <th className="py-1 pr-2 font-medium">{firstColLabel}</th>
                    <th className="py-1 pr-2 font-medium">Shown</th>
                    <th className="py-1 pr-2 font-medium">Accepted</th>
                    <th className="py-1 font-medium">Rate</th>
                </tr>
            </thead>
            <tbody>
                {rows.length === 0 ? (
                    <tr>
                        <td className="py-1 text-grey-700" colSpan={4}>
                            No data
                        </td>
                    </tr>
                ) : (
                    rows.map((row) => (
                        <tr key={row.label} className="border-t border-grey-100">
                            <td className="py-1 pr-2">{row.label}</td>
                            <td className="py-1 pr-2">{row.shown}</td>
                            <td className="py-1 pr-2">{row.accepted}</td>
                            <td className="py-1">{row.acceptance_rate}%</td>
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    </div>
)

const WinBackOffersReportAnalytics = () => {
    const dispatch = useAppDispatch()
    const country = useCurrentCountry()
    const { analytics, analyticsLoading, filters } = useAppSelector(
        (state) => state.winBackOffersReport,
    )

    // Share the same live filters as the list (kept in Redux by the filter bar),
    // so the analytics summary always matches the table below it. The
    // current-month default lives in the slice's initialState. Depend on a
    // serialized key so the effect only refetches when the filters truly change.
    const filtersKey = JSON.stringify(filters)

    useEffect(() => {
        dispatch(fetchWinBackOffersAnalytics({ filters, country }))
    }, [dispatch, filtersKey, country])

    const renderBody = () => {
        if (analyticsLoading && !analytics) {
            return <p className="text-md text-grey-700">Loading analytics...</p>
        }

        if (!analytics) {
            return <p className="text-md text-grey-700">No analytics available.</p>
        }

        const reasonRows: BreakdownRow[] = analytics.by_reason.map((row) => ({
            label: row.reason_label,
            shown: row.shown,
            accepted: row.accepted,
            acceptance_rate: row.acceptance_rate,
        }))

        const dealRows: BreakdownRow[] = analytics.by_deal.map((row) => ({
            label: row.deal,
            shown: row.shown,
            accepted: row.accepted,
            acceptance_rate: row.acceptance_rate,
        }))

        return (
            <div className="flex flex-col gap-6">
                <div className="flex flex-wrap gap-3">
                    <MetricCard label="Offers shown" value={analytics.offers_shown} />
                    <MetricCard label="Offers accepted" value={analytics.offers_accepted} />
                    <MetricCard label="Acceptance rate" value={`${analytics.acceptance_rate}%`} />
                    <MetricCard label="Offers declined" value={analytics.offers_declined} />
                    <MetricCard
                        label="CS bookings dismissed"
                        value={analytics.cs_bookings_dismissed}
                    />
                    <MetricCard label="Repeat acceptances" value={analytics.repeat_acceptances} />
                    <MetricCard
                        label="Accepters still active"
                        value={analytics.accepters_still_active}
                    />
                </div>
                <div className="flex flex-col gap-3">
                    <h3 className="text-md font-bold uppercase tracking-wide text-grey-700">
                        Breakdowns
                    </h3>
                    <div className="flex flex-wrap gap-4">
                        <BreakdownCard
                            title="By churn reason"
                            firstColLabel="Reason"
                            rows={reasonRows}
                        />
                        <BreakdownCard title="By offer deal" firstColLabel="Deal" rows={dealRows} />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <section className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                <h2 className="text-xl font-bold text-grey-900">Win-Back Success</h2>
                <p className="text-md text-grey-700 mb-2">
                    Funnel and breakdowns for offers shown in the selected period.
                </p>
            </div>
            {renderBody()}
        </section>
    )
}

export default WinBackOffersReportAnalytics
